import { useCallback, useState } from 'react';

import { isFuture } from 'date-fns/isFuture';
import { isDate } from 'date-fns/isDate';

import type { CurrencyISO } from '@/constants/currencies';
import type { TransactionInput } from './useAddTransactionToPortfolio';

interface FormFieldValues {
  ticker: {
    symbol: string;
    exchangeCode: string;
    companyName: string;
    currencyISO: CurrencyISO | null;
  } | null;
  date: Date | null;
  type: 'BUY' | 'SELL';
  shares: number | null;
  price: number | null;
}

type FormFieldKey = keyof FormFieldValues;

interface FormFieldErrors {
  ticker: string | null;
  date: string | null;
  shares: string | null;
  price: string | null;
}

type Args = {
  initialValues?: Partial<FormFieldValues>;
};

const defaultValues: FormFieldValues = {
  ticker: null,
  date: new Date(),
  type: 'BUY',
  shares: null,
  price: null,
};

export const getTransactionFromFieldValues = (
  values: FormFieldValues
): TransactionInput | null => {
  if (
    !values.ticker?.symbol ||
    !values.ticker?.exchangeCode ||
    !values.date ||
    !values.shares ||
    !values.price
  ) {
    return null;
  }
  return {
    type: values.type,
    exchangeCode: values.ticker.exchangeCode,
    symbol: values.ticker.symbol,
    transactionDate: values.date.toISOString().split('T')[0],
    units: values.shares,
    amount: values.price,
  };
};

export const useTransactionForm = ({ initialValues }: Args = {}) => {
  const [values, setValues] = useState<FormFieldValues>({
    ...defaultValues,
    ...initialValues,
  });

  const [errors, setErrors] = useState<FormFieldErrors>({
    ticker: null,
    date: null,
    shares: null,
    price: null,
  });

  const validate = useCallback(() => {
    const nextErrors: FormFieldErrors = {
      ticker: null,
      date: null,
      shares: null,
      price: null,
    };

    if (!values.ticker?.symbol) {
      nextErrors.ticker = 'Ticker cannot be empty';
    }

    if (!isDate(values.date)) {
      nextErrors.date = 'Date cannot be empty';
    }

    if (isDate(values.date) && isFuture(values.date)) {
      nextErrors.date = 'Date cannot be in the future';
    }

    if (!values.shares) {
      nextErrors.shares = 'Shares cannot be empty';
    }

    if (!values.price) {
      nextErrors.price = 'Price cannot be empty';
    }

    setErrors(nextErrors);

    return Object.values(nextErrors).some((error) => error) === false;
  }, [values]);

  const update = useCallback(
    <T extends FormFieldKey>(field: T, value: FormFieldValues[T]) => {
      setValues((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const reset = useCallback(() => {
    setValues(({ ticker }) => {
      console.log(ticker, {
        ...defaultValues,
        ticker,
      });
      return {
        ...defaultValues,
        ticker,
      };
    });
    setErrors({
      ticker: null,
      date: null,
      shares: null,
      price: null,
    });
  }, []);

  return {
    values,
    errors,
    validate,
    update,
    reset,
  };
};
